import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, MagnifyingGlass, Warning, FirstAid, ArrowClockwise, ArrowUp, TrendUp } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { TID } from "@/constants/testIds";

export default function LateInjuryModal({ open, onClose, pool = [], onApplied }) {
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [beneficiary, setBeneficiary] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [espnMatches, setEspnMatches] = useState([]);
  const [espnLoading, setEspnLoading] = useState(false);
  // Cascade upgrade preview: null = not previewed yet, {} = previewed
  const [cascade, setCascade] = useState(null);
  const [cascadeLoading, setCascadeLoading] = useState(false);
  const [cascadeEnabled, setCascadeEnabled] = useState(true);

  useEffect(() => {
    if (!open) {
      setQuery("");
      setSelected(null);
      setBeneficiary(null);
      setSuggestions([]);
      setEspnMatches([]);
      setCascade(null);
      return;
    }
    setEspnLoading(true);
    api.injuriesSlate()
      .then((d) => setEspnMatches(d.matches || []))
      .catch(() => {})
      .finally(() => setEspnLoading(false));
  }, [open]);

  useEffect(() => {
    if (!selected) { setCascade(null); return; }
    (async () => {
      const s = await api.suggestBeneficiary(selected.dk_id);
      setSuggestions(s.suggestions || []);
      if (s.suggestions?.[0]) setBeneficiary(s.suggestions[0]);
    })();
  }, [selected]);

  // Whenever the user picks a beneficiary (or unpicks), fetch a fresh cascade
  // preview so they can see the salary-savings upgrades before confirming.
  useEffect(() => {
    if (!selected) return;
    let cancelled = false;
    setCascadeLoading(true);
    api.injuryUpgradeCascade({
      ruled_out_dk_id: String(selected.dk_id),
      beneficiary_dk_id: beneficiary ? String(beneficiary.dk_id) : null,
      apply: false,
    })
      .then((d) => { if (!cancelled) setCascade(d); })
      .catch(() => { if (!cancelled) setCascade(null); })
      .finally(() => { if (!cancelled) setCascadeLoading(false); });
    return () => { cancelled = true; };
  }, [selected, beneficiary]);

  if (!open) return null;

  const filtered = query.trim()
    ? pool.filter((p) => (p.name || "").toLowerCase().includes(query.toLowerCase())).slice(0, 12)
    : pool.filter((p) => ["RB", "WR", "TE"].includes(p.position)).sort((a, b) => (b.salary || 0) - (a.salary || 0)).slice(0, 12);

  const apply = async () => {
    if (!selected) return;
    setLoading(true);
    try {
      if (cascadeEnabled && cascade && cascade.cascade_upgrades_count > 0) {
        // Apply the full cascade (updates saved lineups AND registers the injury out).
        const res = await api.injuryUpgradeCascade({
          ruled_out_dk_id: String(selected.dk_id),
          beneficiary_dk_id: beneficiary ? String(beneficiary.dk_id) : null,
          apply: true,
        });
        toast.success(
          `${selected.name} OUT · ${res.cascade_upgrades_count} salary-savings upgrades applied across ${res.lineups_mutated} lineups (+${res.total_projected_gain} proj)`,
        );
      } else {
        // Plain injury-out (no cascade upgrades OR user disabled cascade)
        await api.lateOut(selected.dk_id, beneficiary?.dk_id);
        toast.success(`${selected.name} marked OUT. ${beneficiary ? `${beneficiary.name} boosted.` : ""}`);
      }
      onApplied?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to apply");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div
        data-testid={TID.lateInjuryModal}
        onClick={(e) => e.stopPropagation()}
        className="bg-[#0a0a0a] border-2 border-[#FF3B30] mm-ai-glow w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col"
      >
        <div className="px-6 py-4 border-b border-zinc-800 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Warning size={22} className="text-[#FF3B30]" weight="fill" />
            <div>
              <div className="heading text-lg font-black uppercase">Late Injury Out</div>
              <div className="overline">Rule a player out & auto-boost beneficiary</div>
            </div>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto space-y-6">
          {/* ESPN auto-populate */}
          <EspnAutoPopulate
            matches={espnMatches}
            loading={espnLoading}
            onPick={(m) => {
              const poolMatch = pool.find((p) => String(p.dk_id) === String(m.dk_id));
              if (poolMatch) {
                setSelected(poolMatch);
                setQuery(poolMatch.name);
                toast.info(`Loaded ${poolMatch.name} (${m.status}) — pick beneficiary`);
              }
            }}
            selectedId={selected?.dk_id}
          />

          {/* Search player */}
          <div>
            <div className="overline mb-2">1. Select the player ruled OUT</div>
            <div className="relative mb-3">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <input
                data-testid={TID.lateInjurySearch}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Type player name…"
                className="w-full bg-black border border-zinc-800 focus:border-[#FF3B30] outline-none pl-9 pr-3 py-2 text-sm"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-1 max-h-[200px] overflow-y-auto">
              {filtered.map((p) => (
                <button
                  key={p.dk_id}
                  onClick={() => setSelected(p)}
                  className={`text-left px-3 py-2 border transition-colors text-xs mono ${
                    selected?.dk_id === p.dk_id ? "border-[#FF3B30] bg-[#FF3B30]/10" : "border-zinc-800 hover:border-zinc-600"
                  }`}
                >
                  <div className="font-medium text-white">{p.name}</div>
                  <div className="text-zinc-500 text-[10px]">
                    {p.position} · {p.team} · ${p.salary?.toLocaleString()}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Beneficiary */}
          {selected && (
            <div>
              <div className="overline mb-2">2. Beneficiary (auto-suggested; override if you want)</div>
              {suggestions.length === 0 ? (
                <div className="text-xs text-zinc-500">No same-team/position candidates found.</div>
              ) : (
                <div className="space-y-2">
                  {suggestions.map((s) => (
                    <button
                      key={s.dk_id}
                      onClick={() => setBeneficiary(s)}
                      className={`w-full text-left flex justify-between items-center px-3 py-2 border transition-colors ${
                        beneficiary?.dk_id === s.dk_id ? "border-[#39FF14] bg-[#39FF14]/10" : "border-zinc-800 hover:border-zinc-600"
                      }`}
                    >
                      <div>
                        <div className="text-sm font-medium">{s.name}</div>
                        <div className="text-[10px] text-zinc-500 mono">{s.position} · {s.team}</div>
                      </div>
                      <div className="text-xs mono text-[#39FF14]">${s.salary?.toLocaleString()}</div>
                    </button>
                  ))}
                  <button
                    onClick={() => setBeneficiary(null)}
                    className="w-full text-xs text-zinc-500 hover:text-white py-1"
                  >
                    (no beneficiary — just remove player)
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Cascade upgrade preview */}
          {selected && (
            <CascadePreview
              cascade={cascade}
              loading={cascadeLoading}
              enabled={cascadeEnabled}
              onToggle={() => setCascadeEnabled((v) => !v)}
            />
          )}
        </div>

        <div className="px-6 py-4 border-t border-zinc-800 flex items-center justify-between">
          <div className="text-xs text-zinc-500">
            {selected
              ? `Ready: ${selected.name} → ${beneficiary?.name || "no beneficiary"}${
                  cascadeEnabled && cascade?.cascade_upgrades_count
                    ? ` + ${cascade.cascade_upgrades_count} upgrades`
                    : ""
                }`
              : "Select a player…"}
          </div>
          <button
            data-testid={TID.lateInjuryConfirm}
            onClick={apply}
            disabled={!selected || loading}
            className="bg-[#FF3B30] hover:bg-[#e0342a] disabled:opacity-40 text-white font-bold uppercase tracking-widest text-xs py-2.5 px-5 transition-colors"
          >
            {loading
              ? "Applying…"
              : cascadeEnabled && cascade?.cascade_upgrades_count
                ? `Confirm Out + ${cascade.cascade_upgrades_count} Upgrades`
                : "Confirm Injury Out"}
          </button>
        </div>
      </div>
    </div>
  );
}

function CascadePreview({ cascade, loading, enabled, onToggle }) {
  if (loading) {
    return (
      <div className="border border-zinc-800 bg-black/40 p-4 flex items-center gap-2 text-xs text-zinc-500">
        <ArrowClockwise size={12} className="animate-spin" />
        Computing salary-savings upgrades…
      </div>
    );
  }
  if (!cascade) return null;
  const {
    salary_freed_per_lineup: freed = 0,
    lineups_affected = 0,
    cascade_upgrades_count = 0,
    total_projected_gain = 0,
    lineups = [],
  } = cascade;

  return (
    <div
      data-testid="cascade-preview"
      className="border border-[#39FF14]/40 bg-[#39FF14]/5 p-4 space-y-3"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <TrendUp size={16} weight="fill" className="text-[#39FF14]" />
          <div className="overline text-[#39FF14]">3. Salary-Savings Cascade Upgrades</div>
        </div>
        <label className="flex items-center gap-2 text-[10px] mono uppercase tracking-widest text-zinc-400 cursor-pointer">
          <input
            type="checkbox"
            checked={enabled}
            onChange={onToggle}
            className="accent-[#39FF14]"
            data-testid="cascade-toggle"
          />
          Apply on confirm
        </label>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
        <div className="border border-zinc-800 bg-black/50 p-2">
          <div className="overline text-[9px] mb-0.5">Salary Freed / Lineup</div>
          <div className="mono text-[#39FF14] font-bold">${freed.toLocaleString()}</div>
        </div>
        <div className="border border-zinc-800 bg-black/50 p-2">
          <div className="overline text-[9px] mb-0.5">Lineups Affected</div>
          <div className="mono text-white font-bold">{lineups_affected}</div>
        </div>
        <div className="border border-zinc-800 bg-black/50 p-2">
          <div className="overline text-[9px] mb-0.5">Cascade Upgrades</div>
          <div className="mono text-[#39FF14] font-bold">{cascade_upgrades_count}</div>
        </div>
        <div className="border border-zinc-800 bg-black/50 p-2">
          <div className="overline text-[9px] mb-0.5">Total Proj. Gain</div>
          <div className="mono text-[#39FF14] font-bold">+{total_projected_gain}</div>
        </div>
      </div>

      {cascade_upgrades_count === 0 ? (
        <div className="text-[11px] text-zinc-500">
          No salary-savings upgrades available — either every unstarted player already has a max-value option,
          or the beneficiary swap freed too little cap. The injury out will still be applied on confirm.
        </div>
      ) : (
        <div className="space-y-2 max-h-[220px] overflow-y-auto">
          {lineups.slice(0, 5).filter((L) => L.swaps.some((s) => s.stage === "upgrade")).map((L) => (
            <div key={L.lineup_id} className="border border-zinc-800 bg-black/40 p-2 text-[11px]">
              <div className="flex items-center justify-between mb-1">
                <span className="mono text-[9px] uppercase tracking-widest text-zinc-500">Lineup #{L.lineup_id}</span>
                <span className="mono text-[10px] text-[#39FF14]">+{L.net_proj_gain} proj · ${L.cap_remaining.toLocaleString()} left</span>
              </div>
              {L.swaps.filter((s) => s.stage === "upgrade").map((s) => (
                <div key={`up-${s.in.dk_id}-${s.out.dk_id}`} className="flex items-center gap-2 pl-2 py-0.5">
                  <ArrowUp size={9} weight="bold" className="text-[#39FF14] flex-shrink-0" />
                  <span className="text-zinc-400 line-through">{s.out.name}</span>
                  <span className="text-zinc-600">→</span>
                  <span className="text-white font-medium">{s.in.name}</span>
                  <span className="text-[10px] mono text-zinc-500 ml-auto">
                    +${(s.salary_delta).toLocaleString()} · +{s.proj_delta.toFixed(1)} pts
                  </span>
                </div>
              ))}
            </div>
          ))}
          {lineups.filter((L) => L.swaps.some((s) => s.stage === "upgrade")).length > 5 && (
            <div className="text-[10px] mono text-zinc-500 text-center">
              …and {lineups.filter((L) => L.swaps.some((s) => s.stage === "upgrade")).length - 5} more lineups will receive similar upgrades.
            </div>
          )}
        </div>
      )}

      <div className="text-[10px] text-zinc-500 leading-relaxed border-t border-zinc-900 pt-2">
        Only upgrades players whose games have <span className="text-[#39FF14]">not kicked off yet</span>.
        Locks in higher projections without violating DK's late-swap rules.
      </div>
    </div>
  );
}

function EspnAutoPopulate({ matches, loading, onPick, selectedId }) {
  const critical = (matches || []).filter((m) => ["OUT", "IR", "PUP", "DOUBTFUL"].includes(m.status));
  return (
    <div className="bg-[#FF3B30]/5 border border-[#FF3B30]/40 p-4">
      <div className="flex items-center gap-2 mb-2">
        <FirstAid size={16} weight="fill" className="text-[#FF3B30]" />
        <div className="overline text-[#FF3B30]">Sleeper Injury Feed · Auto-populate</div>
        {loading && <ArrowClockwise size={12} className="animate-spin text-zinc-500" />}
      </div>
      {loading ? (
        <div className="text-xs text-zinc-500">Fetching Sleeper injury feed…</div>
      ) : critical.length === 0 ? (
        <div className="text-xs text-zinc-500">
          No OUT/IR/DOUBTFUL players currently flagged on your slate. Use manual search below.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {critical.slice(0, 8).map((m) => (
            <button
              key={m.dk_id}
              data-testid={TID.injurySyncModalBtn}
              onClick={() => onPick(m)}
              className={`text-left px-3 py-2 border transition-colors text-xs mono ${
                String(selectedId) === String(m.dk_id)
                  ? "border-[#FF3B30] bg-[#FF3B30]/15"
                  : "border-zinc-800 hover:border-[#FF3B30]"
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-white truncate">{m.player_name}</span>
                <span className={`text-[10px] mono px-2 py-0.5 border font-bold shrink-0 ${
                  m.status === "OUT" || m.status === "IR" || m.status === "PUP"
                    ? "border-[#FF3B30] text-[#FF3B30]"
                    : "border-[#FFCC00] text-[#FFCC00]"
                }`}>
                  {m.status}
                </span>
              </div>
              <div className="text-[10px] text-zinc-500 mt-0.5">
                {m.position} · {m.team} · ${m.salary?.toLocaleString()}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
